import { Button } from 'primereact/button';
import { Controller } from 'react-hook-form';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import InputErrorMessage from 'components/UI/InputErrorMessage/InputErrorMessage';
import LabelName from 'components/UI/LabelName/LabelName';
import { useUserDetailsContext } from 'context/userDetailsContext';
import Dropzone from 'react-dropzone';
import CaseListDropDown from 'components/CaseListDropDown/CaseListDropDown';
import { Chips } from 'primereact/chips';
import { sanitizeHtmlColorContent } from 'utils/utils';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { useRef } from 'react';

const MailCreateForm = ({ caseId, onSubmit, handleSubmit, handleClose, control, errors, watch, trigger }) => {
  const { userDetails } = useUserDetailsContext();
  const { addToast } = useToast();
  const maxFiles = 20;
  const chipsRef = useRef(null);
  const chipsRefCc = useRef(null);
  const chipsRefBcc = useRef(null);

  // Helper function to check for duplicate emails
  const checkForDuplicates = (emails, fieldName) => {
    if (!emails || emails.length === 0) return null;

    const trimmedEmails = emails.map((email) => email.trim().toLowerCase());
    const uniqueEmails = new Set(trimmedEmails);

    // Check for duplicates within the same field
    if (trimmedEmails.length !== uniqueEmails.size) {
      const duplicates = trimmedEmails.filter((email, index) => trimmedEmails.indexOf(email) !== index);
      return `Duplicate email found in ${fieldName}: ${duplicates[0]}`;
    }

    return null;
  };

  // Helper function to check for duplicates across fields
  const checkAcrossFields = (currentEmails, currentFieldName, otherFields) => {
    if (!currentEmails || currentEmails.length === 0) return null;

    const currentTrimmed = currentEmails.map((email) => email.trim().toLowerCase());

    for (const [fieldName, fieldValue] of Object.entries(otherFields)) {
      if (fieldValue && Array.isArray(fieldValue)) {
        const otherTrimmed = fieldValue.map((email) => email.trim().toLowerCase());

        for (const email of currentTrimmed) {
          if (otherTrimmed.includes(email)) {
            return `Email ${email} is already added in ${fieldName}`;
          }
        }
      }
    }

    return null;
  };

  const handleOnBlur = (e, field, reference) => {
    const inputEl = reference.current?.element?.querySelector('input');
    const inputValue = inputEl?.value?.trim();

    if (inputValue) {
      const alreadyExists = field.value?.includes(inputValue);
      if (!alreadyExists) {
        field.onChange([...(field.value || []), inputValue]);
      }
      inputEl.value = '';
    }
  };
  return (
    <div className="h-100">
      <form className="F-size14 message-create-form" onSubmit={handleSubmit(onSubmit)}>
        <div className="justify-content-between align-items-center mb-3 mt-3">
          <div className="modal-heading F-size16 text-bold">Compose Mail</div>
          <div className="d-flex align-items-center py-2">
            <div className="col-12">
              <div>
                <div className="d-flex align-items-center py-2 flex-wrap ">
                  {/* To */}
                  <div className="col-md-4 col-12">
                    <LabelName required={true} htmlFor="recipient_list">
                      To
                    </LabelName>
                  </div>
                  <div className="col-md-8 col-12">
                    <Controller
                      name="recipient_list"
                      control={control}
                      rules={{
                        required: 'Required',
                        validate: (value) => {
                          // Check for duplicates within the To field
                          const duplicateCheck = checkForDuplicates(value, 'To');
                          if (duplicateCheck) return duplicateCheck;

                          // Check for duplicates across CC and BCC fields
                          const ccList = watch('cc_list');
                          const bccList = watch('bcc_list');
                          const crossFieldCheck = checkAcrossFields(value, 'To', {
                            Cc: ccList,
                            Bcc: bccList,
                          });
                          if (crossFieldCheck) return crossFieldCheck;

                          // Check if the recipient list contains the user's email
                          if (value.some((email) => email.trim() === userDetails?.business_email)) {
                            return 'You cannot send an email to your own address';
                          }

                          // Check if the email domain ends with .lawft.com
                          const internalDomainRegex = /@.*\.lawft\.com$/i;

                          // Validate each email format using regex
                          const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                          for (const email of value) {
                            const trimmedEmail = email.trim();

                            // Check for internal user email domain
                            if (internalDomainRegex.test(trimmedEmail)) {
                              return 'You cannot send email to internal users';
                            }

                            if (!emailRegex.test(email.trim())) {
                              return `Invalid email format: ${email}`;
                            }
                          }

                          return true; // No error if all validations pass
                        },
                      }}
                      render={({ field }) => (
                        <Chips
                          ref={chipsRef}
                          inputRef={field.ref}
                          id={field.name}
                          value={field.value}
                          onChange={(e) => field.onChange(e.value)}
                          placeholder={field.value?.length ? '' : 'To'}
                          className="w-100 input-shadow ps-2"
                          separator=","
                          onBlur={(e) => {
                            handleOnBlur(e, field, chipsRef);
                            trigger('recipient_list');
                          }}
                        />
                      )}
                    />
                    {errors.recipient_list && <InputErrorMessage>{errors.recipient_list.message}</InputErrorMessage>}
                  </div>
                </div>
                <div className="d-flex align-items-center py-2 flex-wrap ">
                  {/* cc */}
                  <div className="col-md-4 col-12">
                    <LabelName htmlFor="cc_list">Cc</LabelName>
                  </div>
                  <div className="col-md-8 col-12">
                    <Controller
                      name="cc_list"
                      control={control}
                      rules={{
                        validate: (value) => {
                          if (value) {
                            // Check for duplicates within the CC field
                            const duplicateCheck = checkForDuplicates(value, 'Cc');
                            if (duplicateCheck) return duplicateCheck;

                            // Check for duplicates across To and BCC fields
                            const toList = watch('recipient_list');
                            const bccList = watch('bcc_list');
                            const crossFieldCheck = checkAcrossFields(value, 'Cc', {
                              To: toList,
                              Bcc: bccList,
                            });
                            if (crossFieldCheck) return crossFieldCheck;

                            // Check if the recipient list contains the user's email
                            if (value.some((email) => email.trim() === userDetails?.business_email)) {
                              return 'You cannot send an email to your own address';
                            }

                            // Check if the email domain ends with .lawft.com
                            const internalDomainRegex = /@.*\.lawft\.com$/i;
                            // Validate each email format using regex
                            const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                            for (const email of value) {
                              const trimmedEmail = email.trim();

                              // Check for internal user email domain
                              if (internalDomainRegex.test(trimmedEmail)) {
                                return 'You cannot send email to internal users';
                              }

                              if (!emailRegex.test(email.trim())) {
                                return `Invalid email format: ${email}`;
                              }
                            }

                            return true; // No error if all validations pass
                          }
                        },
                      }}
                      render={({ field }) => (
                        <Chips
                          inputRef={field.ref}
                          id={field.name}
                          value={field.value}
                          onChange={(e) => field.onChange(e.value)}
                          placeholder={field.value ? '' : 'cc'}
                          className={'w-100 input-shadow ps-2'}
                          separator=","
                          ref={chipsRefCc}
                          onBlur={(e) => handleOnBlur(e, field, chipsRefCc)}
                        />
                      )}
                    />
                    {errors.cc_list && <InputErrorMessage>{errors.cc_list.message}</InputErrorMessage>}
                  </div>
                </div>
                <div className="d-flex align-items-center py-2 flex-wrap ">
                  {/* bcc */}
                  <div className="col-md-4 col-12">
                    <LabelName htmlFor="bcc_list">Bcc</LabelName>
                  </div>
                  <div className="col-md-8 col-12">
                    <Controller
                      name="bcc_list"
                      control={control}
                      rules={{
                        validate: (value) => {
                          if (value) {
                            // Check for duplicates within the BCC field
                            const duplicateCheck = checkForDuplicates(value, 'Bcc');
                            if (duplicateCheck) return duplicateCheck;

                            // Check for duplicates across To and CC fields
                            const toList = watch('recipient_list');
                            const ccList = watch('cc_list');
                            const crossFieldCheck = checkAcrossFields(value, 'Bcc', {
                              To: toList,
                              Cc: ccList,
                            });
                            if (crossFieldCheck) return crossFieldCheck;

                            // Check if the recipient list contains the user's email
                            if (value.some((email) => email.trim() === userDetails?.business_email)) {
                              return 'You cannot send an email to your own address';
                            }

                            // Check if the email domain ends with .lawft.com
                            const internalDomainRegex = /@.*\.lawft\.com$/i;
                            // Validate each email format using regex
                            const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                            for (const email of value) {
                              const trimmedEmail = email.trim();

                              // Check for internal user email domain
                              if (internalDomainRegex.test(trimmedEmail)) {
                                return 'You cannot send email to internal users';
                              }

                              if (!emailRegex.test(email.trim())) {
                                return `Invalid email format: ${email}`;
                              }
                            }

                            return true; // No error if all validations pass
                          }
                        },
                      }}
                      render={({ field }) => (
                        <Chips
                          inputRef={field.ref}
                          id={field.name}
                          value={field.value}
                          onChange={(e) => field.onChange(e.value)}
                          placeholder={field.value ? '' : 'bcc'}
                          className={'w-100 input-shadow ps-2'}
                          separator=","
                          ref={chipsRefBcc}
                          onBlur={(e) => handleOnBlur(e, field, chipsRefBcc)}
                        />
                      )}
                    />
                    {errors.bcc_list && <InputErrorMessage>{errors.bcc_list.message}</InputErrorMessage>}
                  </div>
                </div>
              </div>
              <div className="d-flex align-items-center py-2 flex-wrap">
                <div className="col-md-4 col-12">
                  <LabelName required={true} htmlFor="subject">
                    Subject
                  </LabelName>
                </div>
                <div className="col-md-8 col-12">
                  <Controller
                    name="subject"
                    control={control}
                    rules={{ required: 'Required' }}
                    defaultValue=""
                    render={({ field }) => (
                      <TextSnippetWrapper
                        type="text"
                        id={field.name}
                        {...field}
                        placeholder="Subject"
                        className={'w-100 input-shadow'}
                        autoComplete="off"
                      />
                    )}
                  />
                  {errors.subject && <InputErrorMessage>Required</InputErrorMessage>}
                </div>
              </div>
              {!caseId && (
                <div className="d-flex align-items-center py-2 flex-wrap">
                  <div className="col-md-4 col-12">
                    <LabelName required={true} htmlFor="subject">
                      Case
                    </LabelName>
                  </div>
                  <div className="col-md-8 col-12">
                    <Controller
                      name="case"
                      control={control}
                      defaultValue={null}
                      rules={{ required: 'Case is required' }} // Adding validation rule
                      render={({ field }) => (
                        <CaseListDropDown {...field} placeholder="Case/Lead Name" noOptionsMessage="No Cases/Leads Found" />
                      )}
                    />
                    {/* Display error for Case field */}
                    {errors.case && <InputErrorMessage>{errors?.case?.message}</InputErrorMessage>}
                  </div>
                </div>
              )}
              <div className="d-flex align-items-start py-2 flex-wrap">
                <div className="col-md-4 col-12">
                  <LabelName htmlFor="html_body">Body</LabelName>
                </div>
                <div className="col-md-8 col-12">
                  <Controller
                    control={control}
                    name="html_body"
                    className="shadow-small"
                    render={({ field: { onChange, value } }) => (
                      <TextSnippetWrapper
                        className="email-quill-editor"
                        type="editor"
                        value={sanitizeHtmlColorContent(value)}
                        onChange={(e) => onChange(e)}
                      />
                    )}
                  />
                  {errors?.html_body && <InputErrorMessage>Please enter your message</InputErrorMessage>}
                </div>
              </div>
              {/* Attachments Field */}
              <div className="d-flex align-items-start py-2 flex-wrap">
                <div className="col-md-4 col-12 mt-2">
                  <LabelName required={false} htmlFor="attachments">
                    Attachments
                  </LabelName>
                </div>
                <div className="col-md-8 col-12 mt-2">
                  <Controller
                    name="attachments"
                    control={control}
                    defaultValue={[]} // Initialize with an empty array
                    render={({ field }) => (
                      <Dropzone
                        onDrop={(acceptedFiles) => {
                          const updatedFiles = [...field.value, ...acceptedFiles];
                          if (updatedFiles?.length > maxFiles) {
                            addToast(
                              false,
                              toastConstant.toasterType.WARN,
                              toastConstant.header.WARNINIG,
                              toastConstant.message.FILE_UPLOAD_LIMIT_EXCEEDED
                            );
                            return;
                          }
                          field.onChange(updatedFiles); // Update react-hook-form state
                        }}
                        accept="application/pdf, application/msword, .docx, .doc, audio/*, video/*,image/*, .heic, .heif, image/heic, image/heif"
                      >
                        {({ getRootProps, getInputProps }) => (
                          <section>
                            <div {...getRootProps()}>
                              <input {...getInputProps()} />
                              <p className="text-center font-bold attachmnet-input input-shadow mb-0">
                                Click or drag file to this area to upload
                              </p>
                            </div>
                          </section>
                        )}
                      </Dropzone>
                    )}
                  />
                  <ul>
                    <Controller
                      name="attachments"
                      control={control}
                      render={({ field }) =>
                        field.value.map((file, index) => (
                          <div className="d-flex mt-2" key={file.path}>
                            <i className="fas fa-paperclip me-2 mt-1"></i>
                            <li className="text-break">{file.name}</li>
                            <i
                              className="icon-delete ms-2 mt-1 pointer"
                              onClick={() => {
                                const updatedFiles = field.value.filter((f, i) => i !== index);
                                field.onChange(updatedFiles); // Update react-hook-form state
                              }}
                            />
                          </div>
                        ))
                      }
                    />
                  </ul>
                </div>
              </div>
              <div className="footer-btn">
                <Button type="button" label="Cancel" className="p-button p-button-secondary outline me-2" onClick={handleClose} />
                <Button type="submit" label="Send" className="p-button p-button-secondary" />
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default MailCreateForm;
